import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  message,
  Popconfirm,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  CoffeeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import HotelFormModal from '../components/HotelFormModal';

const { Option } = Select;

const HotelManagement = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [filterStarLevel, setFilterStarLevel] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

  useEffect(() => {
    fetchHotels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStarLevel]);

  const fetchHotels = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStarLevel) params.append('starLevel', filterStarLevel);

      const response = await axios.get(`${API_BASE}/hotels?${params.toString()}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setHotels(response.data.data);
      }
    } catch (error) {
      message.error('获取酒店列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingHotel(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingHotel(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/hotels/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        message.success('删除成功');
        fetchHotels();
      }
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const getStarColor = (level) => {
    switch (level) {
      case 5: return 'gold';
      case 4: return 'blue';
      case 3: return 'green';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: '酒店名称',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (text, record) => (
        <div>
          <Space>
            <HomeOutlined />
            <span style={{ fontWeight: 500 }}>{text}</span>
          </Space>
          {record.nameEn && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2, paddingLeft: 22 }}>
              {record.nameEn}
            </div>
          )}
        </div>
      )
    },
    {
      title: '星级',
      dataIndex: 'starLevel',
      key: 'starLevel',
      width: 120,
      render: (level) => (
        <Tag color={getStarColor(level)}>
          {'★'.repeat(level)} {level}星
        </Tag>
      )
    },
    {
      title: '城市',
      dataIndex: 'city',
      key: 'city',
      width: 110
    },
    {
      title: '入住/退房',
      key: 'checkTime',
      width: 110,
      render: (_, record) => (
        <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {record.checkInTime || '-'} / {record.checkOutTime || '-'}
        </span>
      )
    },
    {
      title: '早餐',
      dataIndex: 'hasBreakfast',
      key: 'hasBreakfast',
      width: 60,
      render: (hasBreakfast) => (
        hasBreakfast ? <Tag color="green"><CoffeeOutlined /> 含</Tag> : <Tag>无</Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除该酒店吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="酒店管理"
        extra={
          <Space>
            <Select
              placeholder="筛选星级"
              style={{ width: 120 }}
              allowClear
              value={filterStarLevel}
              onChange={setFilterStarLevel}
            >
              <Option value={5}>5星</Option>
              <Option value={4}>4星</Option>
              <Option value={3}>3星</Option>
              <Option value={2}>2星</Option>
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新建酒店
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={hotels}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
          size="small"
        />
      </Card>

      <HotelFormModal
        open={modalVisible}
        editingHotel={editingHotel}
        onCancel={() => setModalVisible(false)}
        onSaved={() => {
          setModalVisible(false);
          fetchHotels();
        }}
      />
    </div>
  );
};

export default HotelManagement;
